import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { Department } from '@app/features/scoping/models/department.model'
import { Role } from '@app/features/scoping/models/role.model'
import { plainToInstance } from 'class-transformer'
import { Dictionary } from 'lodash'
import { ChangeItem, ChangeType } from '../model'
import { getRoleOldSellingRate } from '@app/shared/components/scope-elements/utils/change-request'

const DEFAULT_QTY = 1
const DEFAULT_MOD = 1

interface DeliverableDelta {
  hours: number
  sellingPrice: number
  profit?: number
  markup?: number
}

const ZERO_DELTA: DeliverableDelta = { hours: 0, sellingPrice: 0, profit: 0 }

interface ComponentModifiers {
  newQty: number
  oldQty: number
  newQtyMod: number
  oldQtyMod: number
  newPriceMod: number
  oldPriceMod: number
}

interface DepartmentSelection {
  resolvedDepartments: Department[]
  modifierChanged: boolean
  quantityChanged: boolean
}

interface DepartmentDeltasResult {
  departmentsWithDeltas: Department[]
  newHoursSum: number
  oldHoursSum: number
  newPriceSum: number
  oldPriceSum: number
}

interface EntityWithChangeId {
  changeAuditData?: { identity?: number }
  id: number
}

function withDeltaFields<T>(obj: T): T & Record<string, unknown> {
  return obj as unknown as T & Record<string, unknown>
}

function getChangeItem(entity: EntityWithChangeId, map: Dictionary<ChangeItem>): ChangeItem | undefined {
  return map[entity.changeAuditData?.identity ?? entity.id]
}

function isRemovedChange(entity: EntityWithChangeId, map: Dictionary<ChangeItem>): boolean {
  return getChangeItem(entity, map)?.changeType === ChangeType.REMOVED
}

export function filterNetChangesDeliverables(
  deliverables: Deliverable[],
  changeItemsMap: Dictionary<ChangeItem>,
  originalDeliverables?: Deliverable[]
): Deliverable[] {
  const originalById = new Map((originalDeliverables ?? []).map((d) => [d.id, d]))
  const processedDeliverables = (deliverables ?? [])
    .map((d) => processDeliverable(d, changeItemsMap, originalById))
    .filter((d): d is Deliverable => d != null)
  const removedDeliverables = (originalDeliverables ?? [])
    .filter((orig) => isRemovedChange(orig, changeItemsMap))
    .map(createRemovedDeliverable)
  return [...processedDeliverables, ...removedDeliverables]
}

function getDeliverableTotals(deliverable: Deliverable | undefined): DeliverableDelta {
  if (!deliverable) return { hours: 0, sellingPrice: 0, profit: 0 }
  return {
    hours: deliverable.getTotalRateCardHours(),
    sellingPrice: deliverable.getTotalSellingPrice()?.amount ?? 0,
    profit: deliverable.getTotalProfit()?.amount ?? 0,
    markup: deliverable.getMarkup(),
  }
}

function setDeliverableDelta(
  deliverable: Deliverable,
  current: DeliverableDelta,
  old: Partial<DeliverableDelta>
): void {
  const deliverableWithDelta = withDeltaFields(deliverable)
  deliverableWithDelta['_currentHours'] = current.hours
  deliverableWithDelta['_currentSellingPrice'] = current.sellingPrice
  deliverableWithDelta['_currentProfit'] = current.profit ?? 0
  deliverableWithDelta['_currentMarkup'] = current.markup
  deliverableWithDelta['oldHours'] = old.hours
  deliverableWithDelta['oldSellingPrice'] = old.sellingPrice
  deliverableWithDelta['oldProfit'] = old.profit
  deliverableWithDelta['oldMarkup'] = old.markup
}

function buildComponentsToUse(
  deliverable: Deliverable,
  original: Deliverable | undefined,
  changeItemsMap: Dictionary<ChangeItem>
): ScopeComponent[] | null {
  const originalById = new Map((original?.components ?? []).map((c) => [c.id, c]))
  const processedComponents = deliverable.components
    .map((c) => processComponent(c, changeItemsMap, originalById))
    .filter((c): c is ScopeComponent => c != null)
  const removedComponents = (original?.components ?? [])
    .filter((c) => isRemovedChange(c, changeItemsMap))
    .map(createRemovedComponent)
  const quantityChanged =
    original != null && (deliverable.quantity ?? DEFAULT_QTY) !== (original.quantity ?? DEFAULT_QTY)
  const resolvedComponents =
    processedComponents.length > 0
      ? [...processedComponents, ...removedComponents]
      : quantityChanged
        ? [...deliverable.components, ...removedComponents]
        : removedComponents
  return resolvedComponents.length ? resolvedComponents : null
}

function getDepartmentOldHours(dept: Department): number {
  return (withDeltaFields(dept)['oldHours'] as number | undefined) ?? dept.getTotalRateCardHours()
}

function getDepartmentOldSellingPrice(dept: Department, costPlus: number): number {
  return (
    (withDeltaFields(dept)['oldSellingPrice'] as number | undefined) ??
    dept.getTotalSellingPrice({ markup: costPlus })?.amount ??
    0
  )
}

function sumRemovedHoursAndPrice(components: ScopeComponent[]): { removedHours: number; removedSellingPrice: number } {
  let removedHours = 0
  let removedSellingPrice = 0
  for (const comp of components) {
    const costPlus = comp.costPlusValue ?? 0
    for (const dept of comp.departments ?? []) {
      if (dept.isRemoved) {
        removedHours += getDepartmentOldHours(dept)
        removedSellingPrice += getDepartmentOldSellingPrice(dept, costPlus)
      } else {
        for (const role of dept.roles ?? []) {
          if (role.isRemoved) {
            removedHours += role.oldRateCardHours ?? 0
            removedSellingPrice += getRoleOldSellingRate(role, costPlus) * (role.oldRateCardHours ?? 0)
          }
        }
      }
    }
  }
  return { removedHours, removedSellingPrice }
}

function processDeliverable(
  deliverable: Deliverable,
  changeItemsMap: Dictionary<ChangeItem>,
  originalById: Map<number, Deliverable>
): Deliverable | null {
  const changeItem = getChangeItem(deliverable, changeItemsMap)
  const original = originalById.get(deliverable.id)

  if (changeItem?.changeType === ChangeType.ADDED) {
    const result = plainToInstance(Deliverable, { ...deliverable })
    setDeliverableDelta(result, getDeliverableTotals(deliverable), ZERO_DELTA)
    return result
  }

  if (changeItem?.changeType === ChangeType.REMOVED) {
    const result = plainToInstance(Deliverable, { ...deliverable })
    setDeliverableDelta(result, ZERO_DELTA, getDeliverableTotals(original))
    return result
  }

  const resolvedComponents = buildComponentsToUse(deliverable, original, changeItemsMap)
  if (!resolvedComponents) return null

  const { removedHours, removedSellingPrice } = sumRemovedHoursAndPrice(resolvedComponents)
  const qty = deliverable.quantity ?? DEFAULT_QTY
  const baseHoursPerUnit = deliverable.getTotalRateCardHours() / qty
  const basePricePerUnit = (deliverable.getTotalSellingPrice()?.amount ?? 0) / qty
  const result = plainToInstance(Deliverable, { ...deliverable, components: resolvedComponents })
  const current: DeliverableDelta = {
    hours: Math.max(0, baseHoursPerUnit - removedHours) * qty,
    sellingPrice: Math.max(0, basePricePerUnit - removedSellingPrice) * qty,
    profit: deliverable.getTotalProfit()?.amount ?? 0,
    markup: deliverable.getMarkup(),
  }
  setDeliverableDelta(result, current, getDeliverableTotals(original))
  return result
}

function createRemovedDeliverable(original: Deliverable): Deliverable {
  const components = (original.components ?? []).map(createRemovedComponent)
  const result = plainToInstance(Deliverable, { ...original, components })
  setDeliverableDelta(result, ZERO_DELTA, getDeliverableTotals(original))
  return result
}

function applyComponentDelta(
  componentWithDelta: Record<string, unknown>,
  currentHours: number,
  currentPrice: number,
  oldHours: number,
  oldPrice: number
): void {
  componentWithDelta['_currentHours'] = currentHours
  componentWithDelta['_currentSellingPrice'] = currentPrice
  componentWithDelta['_oldHoursFiltered'] = oldHours
  componentWithDelta['_oldSellingPriceFiltered'] = oldPrice
  componentWithDelta['oldHours'] = oldHours
  componentWithDelta['oldSellingPrice'] = oldPrice
}

function getOldModifier(originalVal: number | undefined, deltaVal: number | undefined, fallback: number): number {
  return originalVal ?? deltaVal ?? fallback
}

function getComponentModifiers(component: ScopeComponent, original: ScopeComponent | undefined): ComponentModifiers {
  const delta = withDeltaFields(component)
  const newQty = component.quantity ?? DEFAULT_QTY
  const newQtyMod = component.quantityModifier ?? DEFAULT_MOD
  const newPriceMod = component.priceModifier ?? DEFAULT_MOD
  return {
    newQty,
    oldQty: getOldModifier(original?.quantity, delta['oldQuantity'] as number | undefined, newQty),
    newQtyMod,
    oldQtyMod: getOldModifier(
      original?.quantityModifier,
      delta['oldQuantityModifier'] as number | undefined,
      newQtyMod
    ),
    newPriceMod,
    oldPriceMod: getOldModifier(original?.priceModifier, delta['oldPriceModifier'] as number | undefined, newPriceMod),
  }
}

function setDepartmentDeltaFields(
  departmentWithDelta: Record<string, unknown>,
  isRemoved: boolean,
  newHours: number,
  oldHours: number,
  newPrice: number,
  oldPrice: number
): void {
  departmentWithDelta['_currentHours'] = isRemoved ? 0 : newHours
  departmentWithDelta['oldHours'] = oldHours
  departmentWithDelta['_currentSellingPrice'] = isRemoved ? 0 : newPrice
  departmentWithDelta['oldSellingPrice'] = oldPrice
}

function setAddedDeltaOnDepartment(dept: Department, costPlus: number): void {
  const departmentWithDelta = withDeltaFields(dept)
  departmentWithDelta['isAdded'] = true
  departmentWithDelta['_currentHours'] = dept.getTotalRateCardHours()
  departmentWithDelta['oldHours'] = 0
  departmentWithDelta['_currentSellingPrice'] = dept.getTotalSellingPrice({ markup: costPlus })?.amount ?? 0
  departmentWithDelta['oldSellingPrice'] = 0
}

function setAddedDeltaOnRole(role: Role): void {
  const roleWithDelta = withDeltaFields(role)
  roleWithDelta['isAdded'] = true
  roleWithDelta['oldRateCardHours'] = 0
  roleWithDelta['oldRateCardCost'] = roleWithDelta['oldRateCardCost'] ?? 0
  roleWithDelta['oldRateCardRate'] = roleWithDelta['oldRateCardRate'] ?? 0
}

function setRemovedDeltaOnDepartment(dept: Department, costPlus: number): void {
  const departmentWithDelta = withDeltaFields(dept)
  departmentWithDelta['_currentHours'] = 0
  departmentWithDelta['oldHours'] = dept.getTotalRateCardHours()
  departmentWithDelta['_currentSellingPrice'] = 0
  departmentWithDelta['oldSellingPrice'] = dept.getTotalSellingPrice({ markup: costPlus })?.amount ?? 0
}

function setRemovedDeltaOnRole(role: Role): void {
  const roleWithDelta = withDeltaFields(role)
  roleWithDelta['oldRateCardHours'] = role.getRateCardHours()
  roleWithDelta['rateCardMinutes'] = 0
  roleWithDelta['oldRateCardRate'] = roleWithDelta['oldRateCardRate'] ?? role.rateCardRate
  roleWithDelta['oldRateCardCost'] = roleWithDelta['oldRateCardCost'] ?? role.rateCardCost
}

function processComponent(
  component: ScopeComponent,
  changeItemsMap: Dictionary<ChangeItem>,
  originalById: Map<number, ScopeComponent>
): ScopeComponent | null {
  const changeItem = getChangeItem(component, changeItemsMap)
  const original = originalById.get(component.id)
  const isAdded =
    changeItem?.changeType === ChangeType.ADDED || (!original && changeItem?.changeType !== ChangeType.REMOVED)

  if (isAdded) return markComponentAsAdded(component)
  if (changeItem?.changeType === ChangeType.REMOVED) {
    return applyOriginalTotalsToComponent(plainToInstance(ScopeComponent, { ...component }), original)
  }

  const selection = buildDepartmentsToUse(component, original)
  const { resolvedDepartments, modifierChanged, quantityChanged } = selection
  const hasNoRelevantChanges = !resolvedDepartments.length && !modifierChanged && !quantityChanged
  if (hasNoRelevantChanges) return null

  const costPlus = component.costPlusValue ?? 0
  const deltas = computeDepartmentDeltas(resolvedDepartments, costPlus)
  const mods = getComponentModifiers(component, original)
  const qtyMod = mods.newQty * mods.newQtyMod
  const priceMod = mods.newPriceMod * mods.newQty * mods.newQtyMod
  const removedHours = deltas.oldHoursSum - deltas.newHoursSum
  const removedPrice = deltas.oldPriceSum - deltas.newPriceSum
  const totalHours = component.getTotalRateCardHours()
  const totalPrice = component.getTotalSellingPrice()?.amount ?? 0
  const useOriginal = resolvedDepartments.length === 0 && !!original

  const currentHours = useOriginal ? totalHours : Math.max(0, totalHours - removedHours * qtyMod)
  const currentPrice = useOriginal ? totalPrice : Math.max(0, totalPrice - removedPrice * priceMod)
  const oldHours = useOriginal ? original!.getTotalRateCardHours() : deltas.oldHoursSum * mods.oldQty * mods.oldQtyMod
  const oldPrice = useOriginal
    ? (original!.getTotalSellingPrice()?.amount ?? 0)
    : deltas.oldPriceSum * mods.oldPriceMod * mods.oldQty * mods.oldQtyMod

  const result = plainToInstance(ScopeComponent, { ...component, departments: deltas.departmentsWithDeltas })
  applyComponentDelta(withDeltaFields(result), currentHours, currentPrice, oldHours, oldPrice)
  return applyOriginalTotalsToComponent(result, original)
}

function markComponentAsAdded(component: ScopeComponent): ScopeComponent {
  const result = plainToInstance(ScopeComponent, { ...component })
  const componentWithDelta = withDeltaFields(result)
  componentWithDelta['isAdded'] = true
  componentWithDelta['_currentHours'] = component.getTotalRateCardHours()
  componentWithDelta['_currentSellingPrice'] = component.getTotalSellingPrice()?.amount ?? 0
  componentWithDelta['oldHours'] = 0
  componentWithDelta['oldSellingPrice'] = 0
  const costPlus = component.costPlusValue ?? 0
  for (const dept of result.departments ?? []) {
    setAddedDeltaOnDepartment(dept, costPlus)
    for (const role of dept.roles ?? []) setAddedDeltaOnRole(role)
  }
  return result
}

function applyOriginalTotalsToComponent(
  component: ScopeComponent,
  original: ScopeComponent | undefined
): ScopeComponent {
  if (!original) return component
  const sourceWithDelta = withDeltaFields(component)
  const oldHours = (sourceWithDelta['_oldHoursFiltered'] as number | undefined) ?? original.getTotalRateCardHours()
  const oldPrice =
    (sourceWithDelta['_oldSellingPriceFiltered'] as number | undefined) ?? original.getTotalSellingPrice()?.amount
  const result = plainToInstance(ScopeComponent, {
    ...component,
    oldSellingPrice: oldPrice,
    oldHours,
    oldMarkup: original.getMarkup(),
  })
  const resultWithDelta = withDeltaFields(result)
  resultWithDelta['_currentHours'] = sourceWithDelta['_currentHours']
  resultWithDelta['_currentSellingPrice'] = sourceWithDelta['_currentSellingPrice']
  resultWithDelta['oldHours'] = oldHours
  resultWithDelta['oldSellingPrice'] = oldPrice
  return result
}

function createRemovedComponent(original: ScopeComponent): ScopeComponent {
  const comp = plainToInstance(ScopeComponent, { ...original })
  const componentWithDelta = withDeltaFields(comp)
  componentWithDelta['_currentHours'] = 0
  componentWithDelta['_currentSellingPrice'] = 0
  componentWithDelta['oldHours'] = original.getTotalRateCardHours()
  componentWithDelta['oldSellingPrice'] = original.getTotalSellingPrice()?.amount ?? 0
  const costPlus = original.costPlusValue ?? 0
  for (const dept of comp.departments ?? []) {
    setRemovedDeltaOnDepartment(dept, costPlus)
    for (const role of dept.roles ?? []) setRemovedDeltaOnRole(role)
  }
  return comp
}

function buildDepartmentsToUse(component: ScopeComponent, original: ScopeComponent | undefined): DepartmentSelection {
  const departmentsWithChanges = (component.departments ?? [])
    .map((dept) => processDepartment(dept))
    .filter((d): d is Department => d != null)
  const hasOriginal = original != null
  const modifierChanged =
    hasOriginal &&
    ((component.quantityModifier ?? DEFAULT_MOD) !== (original.quantityModifier ?? DEFAULT_MOD) ||
      (component.priceModifier ?? DEFAULT_MOD) !== (original.priceModifier ?? DEFAULT_MOD))
  const quantityChanged = hasOriginal && (component.quantity ?? DEFAULT_QTY) !== (original.quantity ?? DEFAULT_QTY)
  const baseDepartments =
    departmentsWithChanges.length > 0
      ? departmentsWithChanges
      : modifierChanged || quantityChanged
        ? (component.departments ?? [])
        : []

  if (!original) return { resolvedDepartments: baseDepartments, modifierChanged, quantityChanged }

  const currentIds = new Set(component.departments?.map((d) => d.id) ?? [])
  const removedDepartments = (original.departments ?? [])
    .filter((od) => !currentIds.has(od.id))
    .map((od) => plainToInstance(Department, { ...od, isRemoved: true }))
  return { resolvedDepartments: [...baseDepartments, ...removedDepartments], modifierChanged, quantityChanged }
}

function getRoleDeltaNumbers(
  role: Role,
  isDeptRemoved: boolean,
  costPlus: number
): { oldRoleHours: number; roleNewHours: number; roleNewPrice: number; roleOldPrice: number } {
  const oldRoleHours = role.oldRateCardHours ?? role.getRateCardHours()
  const roleNewHours = isDeptRemoved || role.isRemoved ? 0 : role.getRateCardHours()
  const rate = role.rateCardCost
    ? role.rateCardCost.getValue() + (role.rateCardCost.getValue() * costPlus) / 100
    : (role.rateCardRate?.getValue() ?? 0)
  const roleNewPrice = isDeptRemoved || role.isRemoved ? 0 : rate * roleNewHours
  const roleOldPrice = getRoleOldSellingRate(role, costPlus) * oldRoleHours
  return { oldRoleHours, roleNewHours, roleNewPrice, roleOldPrice }
}

function computeDepartmentDeltas(resolvedDepartments: Department[], costPlus: number): DepartmentDeltasResult {
  let newHoursSum = 0
  let oldHoursSum = 0
  let newPriceSum = 0
  let oldPriceSum = 0
  const departmentsWithDeltas: Department[] = []

  for (const department of resolvedDepartments) {
    const isDeptRemoved = !!department.isRemoved
    const origHours = department.isRemoved ? department.getTotalRateCardHours() : 0
    const origPrice = department.isRemoved ? (department.getTotalSellingPrice({ markup: costPlus })?.amount ?? 0) : 0
    let deptNewHours = 0
    let deptOldHours = 0
    let deptNewPrice = 0
    let deptOldPrice = 0

    const rolesWithDeltas = department.roles.map((role) => {
      const { oldRoleHours, roleNewHours, roleNewPrice, roleOldPrice } = getRoleDeltaNumbers(
        role,
        isDeptRemoved,
        costPlus
      )
      newHoursSum += roleNewHours
      oldHoursSum += oldRoleHours
      deptNewHours += roleNewHours
      deptOldHours += oldRoleHours
      newPriceSum += roleNewPrice
      oldPriceSum += roleOldPrice
      deptNewPrice += roleNewPrice
      deptOldPrice += roleOldPrice
      return role.isRemoved ? plainToInstance(Role, { ...role, rateCardMinutes: 0 }) : role
    })

    const deptCopy = plainToInstance(Department, { ...department, roles: rolesWithDeltas })
    const deptOldHoursVal = isDeptRemoved ? origHours : deptOldHours
    const deptOldPriceVal = isDeptRemoved ? origPrice : deptOldPrice
    setDepartmentDeltaFields(
      withDeltaFields(deptCopy),
      isDeptRemoved,
      deptNewHours,
      deptOldHoursVal,
      deptNewPrice,
      deptOldPriceVal
    )
    departmentsWithDeltas.push(deptCopy)
  }

  return { departmentsWithDeltas, newHoursSum, oldHoursSum, newPriceSum, oldPriceSum }
}

function roleHasChanges(role: Role): boolean {
  return (
    role.isAdded ||
    role.isRemoved ||
    (role.oldRateCardHours != null && role.oldRateCardHours !== role.getRateCardHours())
  )
}

function processDepartment(department: Department): Department | null {
  if (department.isAdded || department.isRemoved) return department
  const changedRoles = department.roles.filter(roleHasChanges)
  if (!changedRoles.length) return null
  return plainToInstance(Department, { ...department, roles: changedRoles })
}
